import { useCallback, useEffect, useMemo, useState } from 'react'
import cx from 'classnames'
import Foco from 'react-foco'
import { useCurrentFolderStore } from 'hooks/stores/useCurrentFolderStore'
import { useScrollableTable } from 'hooks/useScrollableTable'
import { PropertySearch } from '../PropertySearch/PropertySearch'
import { useDebounce } from 'hooks/useDebounce'
import ExistingPropertyActionItem from './ExistingPropertyActionItem'
import { PropertyForm } from '../PropertyForm/PropertyForm'
import Styles from './PropertyActions.module.scss'
import { BackButton } from 'screens/Finder/components/ViewPreferences/components/ViewModifierControls/common/BackButton/BackButton'
import { PropertiesForForms, Property, PropertyType } from 'api/clients/PropertyClient'
import NewPropertyActionItem from './NewPropertyActionItem'
import { Response } from 'request-dot-js'
import { usePropertiesPermissions } from 'hooks/usePropertiesPermissions'
import SharedStyles from 'screens/Finder/components/ViewPreferences/components/ViewModifierControls/ViewControls.module.scss'

interface Props {
  property?: Property
  availableProperties: Property[]
  hideBackButton?: boolean
  onBackButton?: () => void
  onSearch?: (query: string) => void
  onSelect?: (property: Property) => void
  onCreate?: (property: Property) => void
  onUpdate?: (property: Property, isUndoAction?: boolean) => Promise<Response<{ property: Property }>>
  onCancel: () => void
  onRemove?: () => void
  // This property is only used for styling purposes. We should migrate all the
  // dropdowns to use the DropdownTrigger and deprecate this.
  embedded?: boolean
  calledFromDoc?: boolean
  openPropertyActions?: (propertyId: number | undefined) => void
  fitDropdownContent?: boolean
}

const DebounceInterval = 500

export const PropertyActions = ({
  property,
  availableProperties,
  onCreate,
  onUpdate,
  onRemove,
  onCancel,
  onSearch = () => {},
  onSelect,
  hideBackButton,
  onBackButton,
  embedded = false,
  calledFromDoc = false,
  openPropertyActions,
  fitDropdownContent = false,
}: Props) => {
  const isEdit = !!property?.id
  const [inputQuery, setInputQuery] = useState('')
  const [newActionsVisible, toggleNewActions] = useState(true)
  const [isFormVisible, setIsFormVisible] = useState(false)
  const debouncedSearchTerm = useDebounce(inputQuery, DebounceInterval)
  const searchInProgress = useMemo(() => debouncedSearchTerm.length > 0, [debouncedSearchTerm])
  const [type, setType] = useState<PropertyType>(property?.type || PropertyType.SingleSelect)
  const { canCreate, canEdit } = usePropertiesPermissions(property)
  const { currentFolder } = useCurrentFolderStore()

  const canModifyProperties = property ? canEdit : canCreate

  const propertyTypes = Object.keys(PropertyType)
    .map((key) => ({
      is_new: true,
      type: PropertyType[key as keyof typeof PropertyType],
      name: String(PropertyType[key as keyof typeof PropertyType]),
    }))
    .filter(({ type }) => PropertiesForForms.includes(type))

  // we don't want to show Assignee and Created under Workspace Properties
  const orderedAvailableProperties = availableProperties
    .filter((p) => !p.is_default)
    .sort((a, b) => {
      if (!!a.scope_project_id === !!b.scope_project_id) return 0
      if (!!b.scope_project_id) return -1
      return 1
    })

  const existingActionsEnabled =
    !canModifyProperties ||
    (!searchInProgress && !newActionsVisible) ||
    (searchInProgress && orderedAvailableProperties.length > 0)
  const newActionsEnabled = !searchInProgress && newActionsVisible && propertyTypes.length > 0
  const noSearchResults = searchInProgress && orderedAvailableProperties.length === 0

  const createActionsList = useCallback(() => {
    if (searchInProgress && canModifyProperties) {
      // first item is dummy for creation from search term
      return [{ is_new: true, type: PropertyType.SingleSelect }, ...orderedAvailableProperties]
    } else if (canModifyProperties) {
      return [...propertyTypes, ...orderedAvailableProperties]
    } else if (searchInProgress || existingActionsEnabled) {
      return orderedAvailableProperties
    } else {
      return propertyTypes
    }
  }, [canModifyProperties, searchInProgress, existingActionsEnabled, orderedAvailableProperties, propertyTypes])

  const propertyActions: any[] = createActionsList()
  const availableWorkspaceProperties = propertyActions.filter(({ id, scope_project_id }) => id && !scope_project_id)

  useEffect(() => {
    onSearch(debouncedSearchTerm)
  }, [onSearch, debouncedSearchTerm])

  const selectIndex = useCallback(
    async (index: number) => {
      const selectedItem = propertyActions[index]
      if (selectedItem.is_new) {
        setType(selectedItem.type)
        setIsFormVisible(true)
      } else {
        onSelect?.(selectedItem)
      }
    },
    [propertyActions, onSelect, setIsFormVisible],
  )

  const { selectedIndex, setSelectedIndex } = useScrollableTable({
    enabled: !isFormVisible,
    rowsCount: propertyActions.length - 1,
    onSelect: selectIndex,
    onCancel,
  })

  const workspaceProperties = useCallback(() => {
    if (!availableWorkspaceProperties.length) return null

    return (
      <>
        <div className={Styles.resultHeader}>Workspace properties</div>
        <div>
          {propertyActions.map((propAct, index) => {
            if (!propAct.id || !!propAct.scope_project_id) return null

            return (
              <ExistingPropertyActionItem
                key={`property-${propAct.id}`}
                property={propAct}
                active={index === selectedIndex}
                onMouseEnter={() => setSelectedIndex(index)}
                onClick={() => selectIndex(index)}
              />
            )
          })}
        </div>
      </>
    )
  }, [propertyActions, availableWorkspaceProperties, selectedIndex, setSelectedIndex, selectIndex])

  const newPropertyActions = useCallback(() => {
    return (
      <>
        <div className={Styles.resultHeader}>New property type</div>
        <div>
          {propertyActions.map((key, index) => {
            if (key.id) return <></>
            return (
              <NewPropertyActionItem
                key={key.type}
                type={key.type}
                active={index === selectedIndex}
                onMouseEnter={() => setSelectedIndex(index)}
                onClick={() => selectIndex(index)}
              />
            )
          })}
        </div>
        {workspaceProperties()}
      </>
    )
  }, [propertyActions, selectedIndex, setSelectedIndex, selectIndex, workspaceProperties])

  const createNew = useCallback(() => {
    return (
      <>
        <div className={Styles.resultHeader}>Create a new property</div>
        {propertyActions.map((key, index) => {
          if (key.id) return <></>
          return (
            <NewPropertyActionItem
              key={'create-from-search'}
              type={PropertyType.SingleSelect}
              active={index === selectedIndex}
              onMouseEnter={() => setSelectedIndex(index)}
              onClick={() => selectIndex(index)}
            >
              Create <span className={Styles.bold}>{debouncedSearchTerm}</span>
            </NewPropertyActionItem>
          )
        })}
      </>
    )
  }, [debouncedSearchTerm, propertyActions, selectIndex, selectedIndex, setSelectedIndex])

  const emptyActions = useCallback(() => {
    return (
      <div className={cx(Styles.addPropertyBox, Styles.empty)}>
        <div className={Styles.emptyHeader}>
          No search results matching <span>{debouncedSearchTerm}</span>
        </div>
        <div className={Styles.emptyAction} onClick={() => setInputQuery('')}>
          Clear
        </div>
      </div>
    )
  }, [debouncedSearchTerm])

  if (isEdit) {
    return (
      <Foco onClickOutside={onCancel} component="div">
        <div className={cx(Styles.container, Styles.edit, { [Styles.embedded]: embedded })}>
          <PropertyForm
            onSetType={setType}
            type={type}
            propertyTypes={propertyTypes}
            property={property}
            onRemove={onRemove}
            onUpdate={onUpdate}
          />
        </div>
      </Foco>
    )
  }

  const showBackButton = !(!!hideBackButton && newActionsEnabled)

  const backButtonOnPress = () => {
    if (isFormVisible) {
      setIsFormVisible(false)
      return
    }

    if (!newActionsVisible) {
      toggleNewActions((prev) => !prev)
      return
    }

    onBackButton?.()
  }

  return (
    <>
      <Foco onClickOutside={onCancel} component="div">
        {isFormVisible && (
          <div className={cx(Styles.container, Styles.actions, { [Styles.embedded]: embedded })}>
            <div
              className={cx(Styles.addPropertyBox, SharedStyles.control, {
                [Styles.scrollContainer]: fitDropdownContent,
              })}
            >
              <div className={SharedStyles.title}>
                <BackButton onPress={() => setIsFormVisible(false)} /> Create a new property
              </div>
              <PropertyForm
                onSetType={setType}
                propertyTypes={propertyTypes}
                type={type}
                onCreate={onCreate}
                className={Styles.createNewProperty}
                property={debouncedSearchTerm === '' ? undefined : ({ name: debouncedSearchTerm } as Property)}
                openPropertyActions={openPropertyActions}
              />
            </div>
          </div>
        )}
        {!isFormVisible && (
          <div className={cx(Styles.container, Styles.actions, { [Styles.embedded]: embedded })}>
            <div
              className={cx(Styles.addPropertyBox, SharedStyles.control, {
                [Styles.scrollContainer]: fitDropdownContent,
              })}
            >
              {
                <div
                  className={cx(SharedStyles.title, {
                    [Styles.noBackButton]: !showBackButton,
                    [Styles.calledFromDoc]: calledFromDoc,
                  })}
                >
                  {!!onBackButton && showBackButton && <BackButton onPress={backButtonOnPress} />} Add a Property
                </div>
              }
              {calledFromDoc && (
                <div className={Styles.resultHeader}>
                  Properties added to this document will be applied to all of the files in <b>{currentFolder?.title}</b>{' '}
                  folder.
                </div>
              )}
              <PropertySearch
                autoFocus={true}
                className={Styles.searchWrapper}
                placeholder={'Type property name...'}
                query={inputQuery}
                iconVisible={true}
                onChange={setInputQuery}
                onCancel={onCancel}
              />
              {searchInProgress && canModifyProperties && createNew()}
              {existingActionsEnabled && workspaceProperties()}
              {canModifyProperties && newActionsEnabled && newPropertyActions()}
              {!canModifyProperties && noSearchResults && emptyActions()}
            </div>
          </div>
        )}
      </Foco>
    </>
  )
}
